import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Shield, Clock, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { heroEntrance } from './animations';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-800"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-teal-500/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-teal-600/8 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <div className="text-center space-y-8">
          {/* Badge */}
          <motion.div {...heroEntrance(0.1)} className="inline-block">
            <span
              data-testid="hero-badge"
              className="inline-flex items-center px-5 py-2 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-400 text-sm font-medium tracking-wide uppercase"
            >
              {t.hero.badge}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1 {...heroEntrance(0.25)}
            className="text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight tracking-tight"
          >
            {t.hero.title}
            <span className="block text-teal-400 mt-2">{t.hero.titleAccent}</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed"
          >
            {t.hero.description}
          </motion.p>

          {/* CTAs */}
          <motion.div {...heroEntrance(0.6)}
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
          >
            <Button
              data-testid="hero-cta-primary"
              size="lg"
              className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold px-10 py-7 text-lg transition-all duration-200 hover:scale-105 shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40"
              onClick={() => scrollToSection('cta')}
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              data-testid="hero-cta-secondary"
              size="lg"
              variant="outline"
              className="border-2 border-slate-600 hover:border-teal-500 text-slate-300 hover:text-teal-400 px-10 py-7 text-lg transition-all duration-200 bg-transparent"
              onClick={() => scrollToSection('testimonials')}
            >
              {t.hero.ctaSecondary}
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div {...heroEntrance(0.9)}
            className="grid grid-cols-3 gap-8 pt-12 mt-4 border-t border-slate-700 max-w-xl mx-auto"
          >
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Users size={18} className="text-teal-500 mr-2" />
                <span className="text-2xl md:text-3xl font-bold text-white">50+</span>
              </div>
              <div className="text-xs md:text-sm text-slate-500">{t.hero.stats.clients}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Clock size={18} className="text-teal-500 mr-2" />
                <span className="text-2xl md:text-3xl font-bold text-white">10+</span>
              </div>
              <div className="text-xs md:text-sm text-slate-500">{t.hero.stats.hours}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Shield size={18} className="text-teal-500 mr-2" />
                <span className="text-2xl md:text-3xl font-bold text-white">3x</span>
              </div>
              <div className="text-xs md:text-sm text-slate-500">{t.hero.stats.growth}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
